import { styled } from '@mui/material/styles';
import { AutocompleteValue, Divider } from '@mui/material';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteProps,
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  autocompleteClasses,
} from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { HTMLAttributes, ReactNode, SyntheticEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { onlyUnique } from '../../helpers';

export interface MultiSelectProps<TData>
  extends Omit<
    AutocompleteProps<TData, true, undefined, false>,
    | 'multiple'
    | 'autoComplete'
    | 'includeInputInList'
    | 'disableCloseOnSelect'
    | 'renderInput'
    | 'onChange'
  > {
  propDesc?: keyof TData;
  onChange: (
    value: TData[],
    event?: SyntheticEvent,
    reason?: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<TData>
  ) => void;
  label: ReactNode;
  translationKey?: string;
  selectAll?: boolean;
  showCountBadge?: boolean;
  disabled?: boolean;
  required?: boolean;
  helperText?: ReactNode;
  error?: boolean;
}

export const TagContainer = styled('div')(() => ({
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
}));

export const SelectAll = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: `-${theme.spacing(1)}`,
  backgroundColor: theme.palette.background.paper,
  zIndex: 2,
}));

const CountBadge = styled('span')(({ theme }) => ({
  marginRight: theme.spacing(1),
  minWidth: 20,
  height: 20,
  padding: '0 6px',
  borderRadius: 999,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1,
}));

const MultiSelect = <TData,>({
  propDesc,
  label,
  disabled,
  onChange,
  fullWidth = true,
  loading,
  translationKey,
  options,
  value,
  renderGroup,
  groupBy,
  selectAll,
  showCountBadge = true,
  required,
  error,
  helperText,
  ...others
}: MultiSelectProps<TData>) => {
  const { t } = useTranslation();

  const getLabel = useCallback(
    (option: TData) => {
      let label: string | TData | TData[keyof TData] = propDesc ? option[propDesc] : option;
      label = translationKey ? t(`${translationKey}.${label}`) : label;
      return label as unknown as string;
    },
    [propDesc, t, translationKey]
  );

  const renderTag = useCallback(
    (options: TData[]) => (
      <TagContainer>
        <span>{options.map(getLabel).join()}</span>
      </TagContainer>
    ),
    [getLabel]
  );

  const renderOption = useCallback(
    (
      props: HTMLAttributes<HTMLLIElement>,
      option: TData,
      { selected }: AutocompleteRenderOptionState
    ) => (
      <li {...props}>
        <Checkbox checked={selected} size="small" />
        {getLabel(option)}
      </li>
    ),
    [getLabel]
  );

  const labelSort = (a: TData, b: TData) => (getLabel(a) > getLabel(b) ? 1 : -1);

  const handleSelect = useCallback(
    (
      event: SyntheticEvent,
      value: TData[],
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<TData>
    ) => onChange(value, event, reason, details),
    [onChange]
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      const count = Array.isArray(value) ? value.length : 0;

      return (
        <FormControl fullWidth={fullWidth}>
          <TextField
            {...params}
            label={label}
            required={!!required}
            error={!!error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {showCountBadge && count > 0 && <CountBadge>{count}</CountBadge>}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        </FormControl>
      );
    },
    [fullWidth, label, showCountBadge, value]
  );

  const handleRenderGroup = useMemo(() => {
    if (selectAll && !renderGroup && Array.isArray(value)) {
      const checkedGroup = () => options.length === value.length;

      const onClick = (_group: string, checked: boolean) => {
        const optionsSelected = !checked ? onlyUnique([...value, ...options]) : [];

        onChange(optionsSelected as AutocompleteValue<TData, true, undefined, false>);
      };

      return ({ group, key, children }: AutocompleteRenderGroupParams) => {
        const checked = checkedGroup();
        return (
          <li key={key}>
            <SelectAll>
              <span
                className={autocompleteClasses.option}
                onClick={() => onClick(group, checked)}
                role="option"
                tabIndex={-1}
                onKeyPress={e => (e.key === 'Enter' ? onClick(group, checked) : undefined)}
                aria-selected={checked}
              >
                <Checkbox checked={checked} size="small" />
                {t('shared.labels.selectAll', { length: options.length })}
              </span>
              <Divider />
            </SelectAll>
            {children}
          </li>
        );
      };
    }
    return renderGroup;
  }, [selectAll, renderGroup, value, options, onChange, t]);

  const handleGroupBy = selectAll && !groupBy ? () => '' : groupBy;

  const propsLoading = useMemo(
    () => (loading ? { popupIcon: <CircularProgress size={16} />, disabled: true } : {}),
    [loading]
  );

  return (
    <Autocomplete<TData, true, undefined, false>
      {...others}
      multiple
      disableCloseOnSelect
      value={value}
      disabled={disabled}
      onChange={handleSelect}
      options={(options as []).sort(labelSort)}
      renderTags={showCountBadge ? () => null : renderTag}
      noOptionsText={t('toolbar.labels.noOptionsText')}
      getOptionLabel={getLabel}
      renderOption={renderOption}
      renderInput={renderInput}
      groupBy={handleGroupBy}
      renderGroup={handleRenderGroup}
      {...propsLoading}
    />
  );
};

export default MultiSelect;
